/**
 * @namespace ValidValues
 * @description Contains validated and structured data for use in applications, such as address states.
 */
export namespace ValidValues {

    /**
     * @interface State
     * @description Defines the structure for a state object, containing its full name, abbreviation, and region.
     */
    export interface State {
        name: string;
        abbreviation: string;
        region: string;
    }

    /**
     * @const statesData
     * @description An array of objects containing the full name, abbreviation, and region for US states and territories.
     * This serves as the single source of truth for state-related data.
     * Regions are based on the U.S. Census Bureau definitions.
     */
    export const statesData: State[] = [
        { name: "Alabama", abbreviation: "AL", region: "South" },
        { name: "Alaska", abbreviation: "AK", region: "West" },
        { name: "Arizona", abbreviation: "AZ", region: "West" },
        { name: "Arkansas", abbreviation: "AR", region: "South" },
        { name: "California", abbreviation: "CA", region: "West" },
        { name: "Canal Zone", abbreviation: "CZ", region: "Territory" }, // Note: Obsolete postal code
        { name: "Colorado", abbreviation: "CO", region: "West" },
        { name: "Connecticut", abbreviation: "CT", region: "Northeast" },
        { name: "Delaware", abbreviation: "DE", region: "South" },
        { name: "District of Columbia", abbreviation: "DC", region: "South" },
        { name: "Florida", abbreviation: "FL", region: "South" },
        { name: "Georgia", abbreviation: "GA", region: "South" },
        { name: "Guam", abbreviation: "GU", region: "Territory" },
        { name: "Hawaii", abbreviation: "HI", region: "West" },
        { name: "Idaho", abbreviation: "ID", region: "West" },
        { name: "Illinois", abbreviation: "IL", region: "Midwest" },
        { name: "Indiana", abbreviation: "IN", region: "Midwest" },
        { name: "Iowa", abbreviation: "IA", region: "Midwest" },
        { name: "Kansas", abbreviation: "KS", region: "Midwest" },
        { name: "Kentucky", abbreviation: "KY", region: "South" },
        { name: "Louisiana", abbreviation: "LA", region: "South" },
        { name: "Maine", abbreviation: "ME", region: "Northeast" },
        { name: "Maryland", abbreviation: "MD", region: "South" },
        { name: "Massachusetts", abbreviation: "MA", region: "Northeast" },
        { name: "Michigan", abbreviation: "MI", region: "Midwest" },
        { name: "Minnesota", abbreviation: "MN", region: "Midwest" },
        { name: "Mississippi", abbreviation: "MS", region: "South" },
        { name: "Missouri", abbreviation: "MO", region: "Midwest" },
        { name: "Montana", abbreviation: "MT", region: "West" },
        { name: "Nebraska", abbreviation: "NE", region: "Midwest" },
        { name: "Nevada", abbreviation: "NV", region: "West" },
        { name: "New Hampshire", abbreviation: "NH", region: "Northeast" },
        { name: "New Jersey", abbreviation: "NJ", region: "Northeast" },
        { name: "New Mexico", abbreviation: "NM", region: "West" },
        { name: "New York", abbreviation: "NY", region: "Northeast" },
        { name: "North Carolina", abbreviation: "NC", region: "South" },
        { name: "North Dakota", abbreviation: "ND", region: "Midwest" },
        { name: "Ohio", abbreviation: "OH", region: "Midwest" },
        { name: "Oklahoma", abbreviation: "OK", region: "South" },
        { name: "Oregon", abbreviation: "OR", region: "West" },
        { name: "Pennsylvania", abbreviation: "PA", region: "Northeast" },
        { name: "Puerto Rico", abbreviation: "PR", region: "Territory" },
        { name: "Rhode Island", abbreviation: "RI", region: "Northeast" },
        { name: "South Carolina", abbreviation: "SC", region: "South" },
        { name: "South Dakota", abbreviation: "SD", region: "Midwest" },
        { name: "Tennessee", abbreviation: "TN", region: "South" },
        { name: "Texas", abbreviation: "TX", region: "South" },
        { name: "Utah", abbreviation: "UT", region: "West" },
        { name: "Vermont", abbreviation: "VT", region: "Northeast" },
        { name: "Virgin Islands", abbreviation: "VI", region: "Territory" },
        { name: "Virginia", abbreviation: "VA", region: "South" },
        { name: "Washington", abbreviation: "WA", region: "West" },
        { name: "West Virginia", abbreviation: "WV", region: "South" },
        { name: "Wisconsin", abbreviation: "WI", region: "Midwest" },
        { name: "Wyoming", abbreviation: "WY", region: "West" },
    ];

    /**
     * @const addressStates
     * @description An array of state abbreviations.
     */
    export const addressStates: string[] = statesData.map(state => state.abbreviation);

    /**
     * @const provincesData
     * @description An array of objects containing the full name, abbreviation, and region for Canadian provinces and territories.
     */
    export const provincesData: State[] = [
        { name: "Alberta", abbreviation: "AB", region: "Canada" },
        { name: "British Columbia", abbreviation: "BC", region: "Canada" },
        { name: "Manitoba", abbreviation: "MB", region: "Canada" },
        { name: "New Brunswick", abbreviation: "NB", region: "Canada" },
        { name: "Newfoundland and Labrador", abbreviation: "NL", region: "Canada" },
        { name: "Northwest Territories", abbreviation: "NT", region: "Canada" },
        { name: "Nova Scotia", abbreviation: "NS", region: "Canada" },
        { name: "Nunavut", abbreviation: "NU", region: "Canada" },
        { name: "Ontario", abbreviation: "ON", region: "Canada" },
        { name: "Prince Edward Island", abbreviation: "PE", region: "Canada" },
        { name: "Quebec", abbreviation: "QC", region: "Canada" },
        { name: "Saskatchewan", abbreviation: "SK", region: "Canada" },
        { name: "Yukon", abbreviation: "YT", region: "Canada" },
    ];

    /**
     * @const addressProvinces
     * @description An array of Canadian province/territory abbreviations.
     */
    export const addressProvinces: string[] = provincesData.map(province => province.abbreviation);

    /**
     * @const countries
     * @description Available country options for store addresses.
     */
    export const countries: string[] = ["United States", "Canada"];

}
